import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Plus, Minus, CreditCard, Users } from 'lucide-react';
import PaymentSuccessModal from './PaymentSuccessModal';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

const EventRegistrationModal = ({ event, onClose, isPublic = true, userEmail = null, userName = null }) => {
  const [tariffQuantities, setTariffQuantities] = useState({});
  const [participantNames, setParticipantNames] = useState([]); // Noms des participants
  const [buyerName, setBuyerName] = useState(userName || '');
  const [buyerEmail, setBuyerEmail] = useState(userEmail || '');
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' ou 'virement'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [bankInfoData, setBankInfoData] = useState(null);

  const { currentLanguage } = useLanguage();
  
  // Traductions
  const { translatedText: modalTitleTranslated } = useTranslate("S'inscrire à l'événement");
  const { translatedText: eventTitleTranslated } = useTranslate(event?.title || "");
  const { translatedText: membersOnlyMsgTranslated } = useTranslate("⚠️ Cet événement est réservé aux membres. Veuillez vous connecter pour vous inscrire.");
  const { translatedText: loginBtnTranslated } = useTranslate("Se connecter");
  const { translatedText: selectTicketsTranslated } = useTranslate("Sélectionner vos billets");
  const { translatedText: yourInfoTranslated } = useTranslate("Vos informations");
  const { translatedText: fullNameTranslated } = useTranslate("Nom complet *");
  const { translatedText: emailTranslated } = useTranslate("Email *");
  const { translatedText: paymentMethodTranslated } = useTranslate("Mode de paiement");
  const { translatedText: onlinePaymentTranslated } = useTranslate("Paiement en ligne par carte (Stripe)");
  const { translatedText: bankTransferTranslated } = useTranslate("Paiement par virement bancaire");
  const { translatedText: ticketAfterPaymentTranslated } = useTranslate("Billet validé après réception du paiement");
  const { translatedText: totalTranslated } = useTranslate("Total");
  const { translatedText: ticketTranslated } = useTranslate("billet");
  const { translatedText: ticketsTranslated } = useTranslate("billets");
  const { translatedText: cancelTranslated } = useTranslate("Annuler");
  const { translatedText: processingTranslated } = useTranslate("Traitement...");
  const { translatedText: confirmTransferTranslated } = useTranslate("Confirmer (Virement)");
  const { translatedText: payTranslated } = useTranslate("Payer");
  const { translatedText: confirmRegistrationTranslated } = useTranslate("Confirmer l'inscription");
  const { translatedText: selectTicketErrorTranslated } = useTranslate("Veuillez sélectionner au moins un billet");
  const { translatedText: fillFieldsErrorTranslated } = useTranslate("Veuillez remplir tous les champs obligatoires");
  const { translatedText: registrationErrorTranslated } = useTranslate("Erreur lors de l'inscription");
  const { translatedText: errorOccurredTranslated } = useTranslate("Une erreur est survenue");
  const { translatedText: registrationConfirmedTranslated } = useTranslate("✅ Inscription confirmée ! Vous recevrez un email de confirmation.");
  const { translatedText: bankInfoNotAvailableTranslated } = useTranslate("Informations bancaires non disponibles");

  // Initialiser les quantités à 0
  useEffect(() => {
    const initial = {};
    (event.tariffs || []).forEach(tariff => {
      initial[tariff.id] = 0;
    });
    setTariffQuantities(initial);
  }, [event]);

  // Calculer le total
  const calculateTotal = () => {
    let total = 0;
    (event.tariffs || []).forEach(tariff => {
      const quantity = tariffQuantities[tariff.id] || 0;
      total += tariff.price * quantity;
    });
    return total;
  };

  const getTotalQuantity = () => {
    return Object.values(tariffQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  // Calculer le nombre de places restantes disponibles pour la sélection
  const getAvailablePlaces = () => {
    // Si l'événement a un quota défini et des places restantes
    if (typeof event.places_remaining === 'number') {
      return event.places_remaining;
    }
    // Si quota mais pas de places_remaining calculées, utiliser le quota
    if (event.global_quota || event.max_attendees) {
      return event.global_quota || event.max_attendees;
    }
    // Pas de limite
    return null;
  };

  const handleQuantityChange = (tariffId, delta) => {
    const availablePlaces = getAvailablePlaces();
    const currentTotal = getTotalQuantity();
    const currentValue = tariffQuantities[tariffId] || 0;
    const newValue = currentValue + delta;

    // Empêcher les valeurs négatives
    if (newValue < 0) return;

    // Vérifier si on ne dépasse pas les places disponibles
    if (availablePlaces !== null && delta > 0) {
      const newTotal = currentTotal + delta;
      if (newTotal > availablePlaces) {
        // Ne pas permettre d'ajouter plus de places que disponibles
        return;
      }
    }

    setTariffQuantities(prev => ({
      ...prev,
      [tariffId]: newValue
    }));
  };

  // Vérifier si on peut encore ajouter des places
  const canAddMore = () => {
    const availablePlaces = getAvailablePlaces();
    if (availablePlaces === null) return true; // Pas de limite
    return getTotalQuantity() < availablePlaces;
  };

  // Mettre à jour la liste des participants quand les quantités changent
  useEffect(() => {
    const newParticipants = [];
    (event.tariffs || []).forEach(tariff => {
      const quantity = tariffQuantities[tariff.id] || 0;
      for (let i = 0; i < quantity; i++) {
        newParticipants.push({
          tariff_id: tariff.id,
          tariff_label: tariff.label,
          name: participantNames.find(p => p.tariff_id === tariff.id && p.index === i)?.name || ''
        });
      }
    });
    
    // Mettre à jour seulement si la structure a changé
    if (JSON.stringify(newParticipants.map(p => ({tariff_id: p.tariff_id, tariff_label: p.tariff_label}))) !== 
        JSON.stringify(participantNames.map(p => ({tariff_id: p.tariff_id, tariff_label: p.tariff_label})))) {
      setParticipantNames(newParticipants.map((p, index) => ({
        ...p,
        index: index,
        name: p.name
      })));
    }
  }, [tariffQuantities, event.tariffs]);

  const handleParticipantNameChange = (index, name) => {
    setParticipantNames(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index].name = name;
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation du nombre de places
    if (getTotalQuantity() === 0) {
      setError(currentLanguage === 'fr' ? 'Veuillez sélectionner au moins un billet' : (selectTicketErrorTranslated || 'Veuillez sélectionner au moins un billet'));
      return;
    }

    // Vérification des places disponibles (double sécurité)
    const availablePlaces = getAvailablePlaces();
    if (availablePlaces !== null && getTotalQuantity() > availablePlaces) {
      setError(currentLanguage === 'fr' 
        ? `Il ne reste que ${availablePlaces} place${availablePlaces > 1 ? 's' : ''} disponible${availablePlaces > 1 ? 's' : ''} pour cet événement.`
        : `Only ${availablePlaces} place${availablePlaces > 1 ? 's' : ''} available for this event.`);
      return;
    }

    // Événement complet
    if (availablePlaces === 0) {
      setError(currentLanguage === 'fr' ? 'Cet événement est complet.' : 'This event is full.');
      return;
    }

    if (!buyerName.trim() || !buyerEmail.trim()) {
      setError(currentLanguage === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : (fillFieldsErrorTranslated || 'Veuillez remplir tous les champs obligatoires'));
      return;
    }

    // Validation des noms de participants
    const emptyNames = participantNames.filter(p => !p.name.trim());
    if (emptyNames.length > 0) {
      setError(currentLanguage === 'fr' ? 'Veuillez saisir le nom de chaque participant' : 'Please enter the name of each participant');
      return;
    }

    setLoading(true);

    try {
      // Préparer les tarifs sélectionnés
      const tariffs = [];
      Object.entries(tariffQuantities).forEach(([tariffId, quantity]) => {
        if (quantity > 0) {
          tariffs.push({ tariff_id: tariffId, quantity });
        }
      });

      // Préparer la liste des participants avec noms
      const participants = participantNames.map(p => ({
        name: p.name.trim(),
        tariff_id: p.tariff_id,
        tariff_label: p.tariff_label
      }));

      // Construire les URLs de succès et d'annulation
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?session_id={CHECKOUT_SESSION_ID}&event_success=true`;
      const cancelUrl = currentUrl;

      // Préparer les headers avec le token d'authentification si disponible
      const headers = {
        'Content-Type': 'application/json',
      };
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${event.id}/register-multi`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tariffs,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          participants: participants,
          success_url: successUrl,
          cancel_url: cancelUrl,
          payment_method: paymentMethod
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || (currentLanguage === 'fr' ? 'Erreur lors de l\'inscription' : (registrationErrorTranslated || 'Erreur lors de l\'inscription')));
      }

      const data = await response.json();

      if (data.paid) {
        // Inscription gratuite confirmée - afficher le modal de succès
        setRegistrationData(data.registration || {
          id: data.registration_id,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          tariffs_details: tariffs.map(t => {
            const tariffInfo = event.tariffs.find(et => et.id === t.tariff_id);
            return {
              label: tariffInfo?.label || 'Billet',
              quantity: t.quantity,
              price: tariffInfo?.price || 0
            };
          })
        });
        setPaymentStatus('paid');
        setShowSuccessModal(true);
      } else if (data.checkout_url) {
        // Rediriger vers Stripe
        window.location.href = data.checkout_url;
      } else if (paymentMethod === 'virement') {
        // Inscription par virement - Afficher le modal de succès avec statut "pending"
        setRegistrationData(data.registration);
        setBankInfoData(data.bank_info || event.bank_account_info || (currentLanguage === 'fr' ? 'Informations bancaires non disponibles' : (bankInfoNotAvailableTranslated || 'Informations bancaires non disponibles')));
        setPaymentStatus('pending');
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Erreur inscription:', err);
      setError(err.message || (currentLanguage === 'fr' ? 'Une erreur est survenue' : (errorOccurredTranslated || 'Une erreur est survenue')));
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();
  const totalQty = getTotalQuantity();

  return (
    <>
      {showSuccessModal && (
        <PaymentSuccessModal
          registration={registrationData}
          event={event}
          status={paymentStatus}
          bankInfo={bankInfoData}
          onClose={() => {
            setShowSuccessModal(false);
            onClose();
            window.location.reload();
          }}
        />
      )}
      
      {!showSuccessModal && (
        <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
              >
                {modalTitleTranslated}
              </h3>
              <p 
                className="text-lg text-gray-700"
                style={{ fontFamily: '"Lora", serif' }}
              >
                {currentLanguage === 'fr' ? event.title : (eventTitleTranslated || event.title)}
              </p>
              {/* Afficher les places restantes si un quota existe */}
              {typeof event.places_remaining === 'number' && (
                <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  event.places_remaining === 0 
                    ? 'bg-red-100 text-red-800' 
                    : event.places_remaining <= 10 
                      ? 'bg-orange-100 text-orange-800' 
                      : 'bg-green-100 text-green-800'
                }`}>
                  {event.places_remaining === 0 
                    ? '❌ Complet' 
                    : `🎫 ${event.places_remaining} place${event.places_remaining > 1 ? 's' : ''} restante${event.places_remaining > 1 ? 's' : ''}`}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {!isPublic && !userEmail && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800" style={{ fontFamily: '"Lora", serif' }}>
                {membersOnlyMsgTranslated}
              </p>
              <Button
                className="mt-3 text-white"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                onClick={() => window.location.href = '/membre'}
              >
                {loginBtnTranslated}
              </Button>
            </div>
          )}

          {(isPublic || userEmail) && (
            <form onSubmit={handleSubmit}>
              {/* Sélection des tarifs */}
              <div className="mb-6">
                <h4 
                  className="text-lg font-semibold mb-4"
                  style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                >
                  <Users className="inline w-5 h-5 mr-2" />
                  {selectTicketsTranslated}
                </h4>
                
                <div className="space-y-3">
                  {(event.tariffs || []).map(tariff => (
                    <div key={tariff.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-semibold" style={{ fontFamily: '"Lora", serif' }}>
                          {tariff.label}
                        </div>
                        <div 
                          className="text-lg font-bold"
                          style={{ color: 'rgb(196, 150, 96)' }}
                        >
                          {tariff.price.toFixed(2)} €
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(tariff.id, -1)}
                          disabled={!tariffQuantities[tariff.id] || tariffQuantities[tariff.id] === 0}
                          className="h-8 w-8 p-0"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        
                        <span className="w-8 text-center font-semibold">
                          {tariffQuantities[tariff.id] || 0}
                        </span>
                        
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(tariff.id, 1)}
                          disabled={!canAddMore()}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Noms des participants */}
              {getTotalQuantity() > 0 && (
                <div className="mb-6">
                  <h4 
                    className="text-lg font-semibold mb-4"
                    style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                  >
                    👥 Noms des participants
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Veuillez saisir le nom de chaque participant. Ces noms apparaîtront sur les billets.
                  </p>
                  
                  <div className="space-y-3">
                    {participantNames.map((participant, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold" style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">
                            {participant.tariff_label}
                          </label>
                          <input
                            type="text"
                            value={participant.name}
                            onChange={(e) => handleParticipantNameChange(index, e.target.value)}
                            placeholder="Nom complet du participant"
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {/* Informations acheteur */}
              <div className="mb-6">
                <h4 
                  className="text-lg font-semibold mb-4"
                  style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                >
                  {yourInfoTranslated}
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {fullNameTranslated}
                    </label>
                    <input
                      type="text"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {emailTranslated}
                    </label>
                    <input
                      type="email"
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Mode de paiement */}
              {total > 0 && (
                <div className="mb-6">
                  <h4 
                    className="text-lg font-semibold mb-4"
                    style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                  >
                    {paymentMethodTranslated}
                  </h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe"
                        checked={paymentMethod === 'stripe'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <CreditCard className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                      <span className="font-medium">{onlinePaymentTranslated}</span>
                    </label>
                    
                    <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="payment_method"
                        value="virement"
                        checked={paymentMethod === 'virement'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <span className="mr-2">🏦</span>
                      <div>
                        <span className="font-medium">{bankTransferTranslated}</span>
                        <p className="text-xs text-gray-600">{ticketAfterPaymentTranslated}</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Erreur */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
                  {error}
                </div>
              )}

              {/* Total et Bouton */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold" style={{ fontFamily: '"Lora", serif' }}>
                    {totalTranslated} ({totalQty} {totalQty > 1 ? ticketsTranslated : ticketTranslated})
                  </span>
                  <span 
                    className="text-2xl font-bold"
                    style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                  >
                    {total.toFixed(2)} €
                  </span>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    {cancelTranslated}
                  </Button>
                  
                  <Button
                    type="submit"
                    className="flex-1 text-white"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    disabled={loading || totalQty === 0}
                  >
                    {loading ? (
                      processingTranslated
                    ) : paymentMethod === 'virement' ? (
                      <>
                        🏦 {confirmTransferTranslated}
                      </>
                    ) : total > 0 ? (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {payTranslated} {total.toFixed(2)} €
                      </>
                    ) : (
                      confirmRegistrationTranslated
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
      )}
    </>
  );
};

export default EventRegistrationModal;
